/**
 * Dexie schema for the field PWA's offline cache.
 *
 * Source-of-truth for sync: `pending_submissions` table. The sync
 * engine reads/writes this directly; TanStack Query caches are
 * advisory-only.
 *
 * Migration policy: additive `db.version(N)` blocks for new tables.
 * For destructive changes, gate via `app_meta.schema_version` —
 * if `< MIN_SUPPORTED`, refuse to operate and force re-pair (which
 * wipes the DB).
 */
import Dexie, { type Table, type Transaction } from 'dexie';

// ── Table row types ────────────────────────────────────────────────────

export interface PendingSubmission {
  /** crypto.randomUUID(); also the row PK. */
  client_submission_id: string;
  /** Endpoint to POST to (e.g. /api/v1/data-vault/sampling/events/{id}/field-records). */
  endpoint: string;
  /** Request body (will be JSON.stringified at submit time). */
  payload: unknown;
  /** queued | syncing | synced | failed */
  status: 'queued' | 'syncing' | 'synced' | 'failed';
  /** event_id when applicable; null for ad-hoc captures. */
  event_id: string | null;
  /** Number of POST attempts. Drives exponential backoff. */
  attempts: number;
  /** Last error message (truncated to 500 chars). */
  last_error: string | null;
  /** ISO ms timestamp of last attempt. */
  last_attempt_at: number | null;
  /** ISO ms when this row was first queued. */
  created_at: number;
  /** Server-side ID returned in the success response. Populated for
   *  FieldRecord submissions so the photo-upload pass can find the
   *  parent record. Optional on read; not all submissions return one. */
  server_id?: string | null;
  /** When true, photos linked by client_submission_id still need to
   *  upload after this submission's success. Cleared once all are
   *  uploaded (or none exist). */
  photos_pending?: boolean;
}

export interface CachedSite {
  site_id: string;
  data: unknown; // FieldSite from @argos/api-types
  cached_at: number;
}

export interface CachedWell {
  /** [site_id, well_name].toLowerCase joined with '|' for compound index. */
  site_well_key: string;
  site_id: string;
  well_name: string;
  data: unknown; // FieldWell from @argos/api-types
}

export interface CachedEvent {
  event_id: string;
  site_id: string;
  status: 'planned' | 'in_progress' | 'complete';
  data: unknown; // AssignedEventEnvelope from @argos/api-types
  cached_at: number;
}

export interface CachedPlanItem {
  /** [event_id, well_id] join key. */
  event_well_key: string;
  event_id: string;
  well_id: string;
  data: unknown; // SamplingPlanItem
}

export interface CachedFieldRecord {
  /** [event_id, well_id]; matches the server-side (event_id, well_id) UNIQUE. */
  event_well_key: string;
  event_id: string;
  well_id: string;
  data: unknown; // FieldRecord (server snapshot)
}

export interface LocalFieldRecord {
  /** Same client_submission_id we'll send up at sync time. */
  client_submission_id: string;
  event_id: string;
  well_id: string;
  /** draft | synced | error */
  status: 'draft' | 'synced' | 'error';
  data: unknown; // FieldRecordUpsert
  updated_at: number;
}

export interface CachedAnalyte {
  analyte_id: string;
  data: unknown; // FieldAnalyte
}

export interface CachedValidationRule {
  rule_id: string;
  data: unknown; // ValidationRule
}

export interface CachedProductionDatum {
  /** [site_id, well_name, sample_date, compound] joined. */
  dedup_key: string;
  site_id: string;
  well_name: string;
  sample_date: string;
  compound: string | null;
  kind: 'gauging' | 'analytical';
}

export interface CachedMapTile {
  /** [z, x, y, style] joined. */
  tile_key: string;
  z: number;
  x: number;
  y: number;
  style: string;
  bytes: Blob;
  cached_at: number;
}

export interface CachedFormSchema {
  formId: string;
  schemaVersion: number;
  data: unknown;
}

export interface CachedFlagCode {
  /** Flag code, e.g. "DR". */
  code: string;
  /** Spelled-out label; the UI shows it with any "(…)" suffix stripped. */
  description: string;
}

export interface DeviceRow {
  /** Single-row table: id is always 'self'. */
  id: 'self';
  device_id: string;
  user: unknown;
  paired_at: string;
  expires_at: string;
  unlock_method: 'webauthn' | 'pin';
  salt: Uint8Array;
  iv: Uint8Array;
  wrapped_token: Uint8Array;
  pin_attempts: number;
  credential_id?: Uint8Array;
  prf_supported?: boolean;
}

export interface SyncStateRow {
  key: 'sync_state';
  last_sync_at: number | null;
  last_etag: string | null;
  last_error: string | null;
}

export interface EventLogRow {
  /** Auto-incrementing primary key. */
  id?: number;
  /** e.g. sync.start, sync.fail, auth.pin_fail, gps.well_geofence_override. */
  type: string;
  payload: unknown;
  created_at: number;
}

export interface PhotoBlobRow {
  /** [client_submission_id, field_key] joined. */
  key: string;
  client_submission_id: string;
  field_key: string;
  blob: Blob;
  width_px: number | null;
  height_px: number | null;
  taken_at: number | null;
  gps_lat: number | null;
  gps_lon: number | null;
  gps_accuracy_m: number | null;
}

export interface AppMetaRow {
  key: string;
  value: unknown;
}

// ── Database ───────────────────────────────────────────────────────────

export class FieldDb extends Dexie {
  pending_submissions!: Table<PendingSubmission, string>;
  cached_sites!: Table<CachedSite, string>;
  cached_wells!: Table<CachedWell, string>;
  cached_events!: Table<CachedEvent, string>;
  cached_plan_items!: Table<CachedPlanItem, string>;
  cached_field_records!: Table<CachedFieldRecord, string>;
  local_field_records!: Table<LocalFieldRecord, string>;
  cached_analytes!: Table<CachedAnalyte, string>;
  cached_validation_rules!: Table<CachedValidationRule, string>;
  cached_flag_codes!: Table<CachedFlagCode, string>;
  cached_production_data!: Table<CachedProductionDatum, string>;
  cached_map_tiles!: Table<CachedMapTile, string>;
  cached_form_schemas!: Table<CachedFormSchema, string>;
  device!: Table<DeviceRow, 'self'>;
  sync_state!: Table<SyncStateRow, 'sync_state'>;
  event_log!: Table<EventLogRow, number>;
  photo_blobs!: Table<PhotoBlobRow, string>;
  app_meta!: Table<AppMetaRow, string>;

  constructor() {
    super('argos_field');

    const STORES = {
      // & = primary key, * = multi-entry, ++ = auto-increment.
      pending_submissions:
        '&client_submission_id, status, event_id, [status+created_at]',
      cached_sites: '&site_id',
      cached_wells: '&site_well_key, site_id',
      cached_events: '&event_id, site_id, status',
      cached_plan_items: '&event_well_key, event_id',
      cached_field_records: '&event_well_key, event_id',
      local_field_records:
        '&client_submission_id, [event_id+well_id], status',
      cached_analytes: '&analyte_id',
      cached_validation_rules: '&rule_id',
      cached_production_data: '&dedup_key, site_id',
      cached_map_tiles: '&tile_key',
      cached_form_schemas: '&formId',
      device: '&id',
      sync_state: '&key',
      event_log: '++id, created_at',
      photo_blobs: '&key, client_submission_id',
      app_meta: '&key',
    } as const;

    // Tables that hold server-derived data and should be wiped on a
    // schema/envelope bump so the device re-hydrates cleanly. (device +
    // pending_submissions + photo_blobs + local_field_records are kept —
    // no re-pair, no lost drafts.)
    const purgeCached = async (tx: Transaction) => {
      const names = [
        'cached_sites',
        'cached_wells',
        'cached_events',
        'cached_plan_items',
        'cached_field_records',
        'cached_analytes',
        'cached_validation_rules',
        'cached_production_data',
        'cached_form_schemas',
        'sync_state',
      ];
      await Promise.all(
        names.map((n) => {
          try {
            return tx.table(n).clear();
          } catch {
            return Promise.resolve();
          }
        }),
      );
    };

    this.version(1).stores(STORES);

    // v2 — schema unchanged; purge so older devices re-hydrate (well names
    // + denormalized well coords on plan items).
    this.version(2).stores(STORES).upgrade(purgeCached);

    // v3 — adds cached_flag_codes (tenant gauging flag codes for the
    // wizard's one-tap "dry / no access / obstructed" buttons). Purge
    // again so the new envelope (which now carries flag_codes) lands.
    const STORES_V3 = { ...STORES, cached_flag_codes: '&code' } as const;
    this.version(3).stores(STORES_V3).upgrade(purgeCached);
  }
}

// Singleton — Dexie itself is process-singleton-safe.
let _dbInstance: FieldDb | null = null;

export function getDb(): FieldDb {
  if (_dbInstance == null) _dbInstance = new FieldDb();
  return _dbInstance;
}

/** Drop the entire database. Used after too many wrong PINs or when
 * the user explicitly logs out. */
export async function wipeDb(): Promise<void> {
  if (_dbInstance != null) {
    _dbInstance.close();
    _dbInstance = null;
  }
  await Dexie.delete('argos_field');
}
